import { z } from 'zod';
/**
 * The domains an organisation sends from.
 *
 * The three states are `found`, `missing` and `unknown`, and the third is not a
 * failure: it means the lookup did not complete, which is a different answer
 * from "the record is not there". Collapsing them would tell somebody their DNS
 * is wrong when the network hiccuped, and send them off to edit records that
 * were already correct.
 *
 * Nothing here says "verified". Presence is what a TXT lookup can establish;
 * walking the SPF chain or verifying the key needs the receiver's view and a
 * real message.
 */
const recordStateSchema = z.enum(['found', 'missing', 'unknown']);
/**
 * A record to publish, and where its value comes from.
 *
 * `value` is null for DKIM, always: the key is generated by whoever sends your
 * mail and exists only in their dashboard. A plausible-looking one here would
 * be worse than none, because somebody would paste it.
 */
export const dnsRecordSchema = z.object({
    kind: z.enum(['SPF', 'DKIM', 'DMARC']),
    type: z.literal('TXT'),
    name: z.string(),
    value: z.string().nullable(),
    note: z.string(),
});
export const sendingDomainSchema = z.object({
    domain: z.string(),
    /** What to publish. Present whether or not the check has found anything. */
    records: z.array(dnsRecordSchema),
    /** Issued by whoever sends for you. Empty until somebody supplies it. */
    dkimSelector: z.string(),
    spf: recordStateSchema,
    dkim: recordStateSchema,
    dmarc: recordStateSchema,
    /**
     * SPF and DKIM together — what mailbox providers actually require of a bulk
     * sender. DMARC is reported and not counted: strongly recommended, not yet
     * universally enforced.
     */
    ready: z.boolean(),
    /** Null until the first check. Distinct from a check that found nothing. */
    checkedAt: z.iso.datetime().nullable(),
});
export const sendingDomainListSchema = z.object({ domains: z.array(sendingDomainSchema) });
export const addSendingDomainRequestSchema = z.object({
    /**
     * A bare domain — no scheme, no path, no address.
     *
     * Checked here because every later lookup is built from it: a value with a
     * slash in it produces a DNS name nobody could ever have published, and a
     * confident "missing" about it.
     */
    domain: z
        .string()
        .trim()
        .toLowerCase()
        .min(3)
        .max(253)
        .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/, 'that is not a domain'),
    /** Optional: a domain can be added before the provider has issued one. */
    dkimSelector: z
        .string()
        .trim()
        .max(63)
        .regex(/^[a-z0-9._-]*$/i, 'a selector is letters, digits, dots, dashes and underscores')
        .optional(),
});
export const addSendingDomainResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('added') }),
    z.object({ status: z.literal('already-there') }),
]);
export const domainCheckResponseSchema = z.object({
    spf: recordStateSchema,
    dkim: recordStateSchema,
    dmarc: recordStateSchema,
    ready: z.boolean(),
    checkedAt: z.iso.datetime(),
});
//# sourceMappingURL=sendingDomains.js.map